import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { 
  getNotifications, 
  markNotificationRead, 
  markAllNotificationsRead 
} from "@/services/edgeFunctionWrappers/notificationsManagementWrapper";

interface Notification {
  id: string;
  type: string;
  payload: any;
  sent_at: string;
  read_at: string | null;
  booking_id?: string;
  booking?: {
    class_id: string;
    class_date: string;
    time: string;
    class: {
      name: string;
      time: string;
      instructor: string;
    };
  };
}

interface FormattedNotification extends Notification {
  message: string;
  timeAgo: string;
}

export const useNotifications = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language === "fr" ? fr : enUS;

  const { data: notifications, isLoading, error } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const { data, error } = await getNotifications(10);
      if (error) throw error;
      return (data || []) as Notification[];
    },
  });

  const formatMessage = (notification: Notification): string => {
    const { type, payload, booking } = notification;

    switch (type) {
      case "ADMIN_MESSAGE":
        return payload?.title || payload?.text || i18n.t("notifications.adminMessage", { ns: "appui" });
      
      case "BOOKING_REMINDER":
        // BOOKING_REMINDER = rappel pour utilisateur inactif (pas de cours spécifique)
        return i18n.t("notifications.bookingReminder", { ns: "appui" });
      
      case "SESSION_REMINDER":
        // SESSION_REMINDER = rappel pour une séance réservée aujourd'hui
        const className = payload?.class_name || booking?.class?.name || i18n.t("notifications.sessionDefault", { ns: "appui" });
        const time = payload?.time || booking?.time || booking?.class?.time;
        if (time) {
          return i18n.t("notifications.sessionReminderWithTime", { ns: "appui", className, time });
        }
        return i18n.t("notifications.sessionReminder", { ns: "appui", className });
      
      case "NEW_BOOKING_TODAY":
        const newBookingClass = payload?.class_name || booking?.class?.name || i18n.t("notifications.aClass", { ns: "appui" });
        const bookingClientName = payload?.client_name || "";
        if (bookingClientName) {
          return i18n.t("notifications.newBookingWithName", { ns: "appui", clientName: bookingClientName, className: newBookingClass });
        }
        return i18n.t("notifications.newBooking", { ns: "appui", className: newBookingClass });
      
      case "CANCELLATION_TODAY":
        const cancelClass = payload?.class_info?.class_name || booking?.class?.name || i18n.t("notifications.class", { ns: "appui" });
        const cancelTime = payload?.class_info?.class_time || booking?.time || "";
        const cancelClientName = payload?.client_name || "";
        if (cancelClientName) {
          return i18n.t("notifications.cancellationWithName", { ns: "appui", clientName: cancelClientName, className: cancelClass, time: cancelTime });
        }
        return i18n.t("notifications.cancellation", { ns: "appui", className: cancelClass, time: cancelTime });
      
      default:
        return i18n.t("notifications.default", { ns: "appui" });
    }
  };

  // date-fns v3 lève `RangeError: Invalid time value` sur une date illisible :
  // sans garde, une seule notification mal horodatée cassait tout le popover.
  const safeTimeAgo = (value: unknown): string => {
    const date = value ? new Date(value as string) : null;
    if (!date || Number.isNaN(date.getTime())) return "";
    return formatDistanceToNow(date, { addSuffix: true, locale });
  };

  const formattedNotifications: FormattedNotification[] = (notifications || []).map((notif) => ({
    ...notif,
    message: formatMessage(notif),
    timeAgo: safeTimeAgo(notif.sent_at),
  }));

  const markAsRead = async (notificationId: string) => {
    const { error } = await markNotificationRead(notificationId);
    if (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    const { error } = await markAllNotificationsRead();
    if (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  return {
    notifications: formattedNotifications,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
  };
};
